import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Calendar,
  CalendarRange,
  Clock,
  Loader2,
  PieChart,
  Repeat,
} from 'lucide-react';
import { adminAPI } from '../../services/api';

const viewOptions = [
  { id: 'daily', label: 'Quotidien', icon: Calendar },
  { id: 'weekly', label: 'Hebdomadaire', icon: CalendarRange },
  { id: 'monthly', label: 'Mensuel', icon: PieChart },
  { id: 'yearly', label: 'Annuel', icon: BarChart3 },
];

const AdminAnalyticsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [view, setView] = useState('daily');
  const [data, setData] = useState({
    totals: {
      today: 0,
      thisWeek: 0,
      thisMonth: 0,
      thisYear: 0,
      allTime: 0,
    },
    daily: [],
    weekly: [],
    monthly: [],
    yearly: [],
  });

  const fetchStats = async (isManualRefresh = false) => {
    if (isManualRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    try {
      const response = await adminAPI.analytics.getVisitStats();
      setData(response.data || {});
    } catch (error) {
      console.error('Failed to load visit statistics', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const currentDataset = useMemo(() => {
    switch (view) {
      case 'weekly':
        return data.weekly || [];
      case 'monthly':
        return data.monthly || [];
      case 'yearly':
        return data.yearly || [];
      case 'daily':
      default:
        return data.daily || [];
    }
  }, [view, data]);

  const maxVisits = currentDataset.reduce(
    (max, item) => Math.max(max, Number(item.visits || 0)),
    0
  );

  const formatLabel = (item) => {
    switch (view) {
      case 'weekly': {
        const start = item.startDate
          ? new Date(item.startDate).toLocaleDateString('fr-FR')
          : '-';
        const end = item.endDate
          ? new Date(item.endDate).toLocaleDateString('fr-FR')
          : '-';
        return `Semaine du ${start} au ${end}`;
      }
      case 'monthly': {
        if (!item.key) return '-';
        const dt = new Date(`${item.key}-01T00:00:00`);
        return dt.toLocaleDateString('fr-FR', {
          month: 'long',
          year: 'numeric',
        });
      }
      case 'yearly':
        return item.key || '-';
      case 'daily':
      default: {
        if (!item.date) return '-';
        const dt = new Date(`${item.date}T00:00:00`);
        return dt.toLocaleDateString('fr-FR', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-10 w-10 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Analytics des Visites</h1>
          <p className="text-gray-400 mt-2">
            Suivi des visites quotidiennes, hebdomadaires, mensuelles et annuelles de votre site.
          </p>
        </div>
        <button
          onClick={() => fetchStats(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isRefreshing}
        >
          {isRefreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Repeat className="h-4 w-4" />
          )}
          <span>Actualiser</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <StatCard
          title="Aujourd'hui"
          value={data?.totals?.today || 0}
          icon={Clock}
          accent="from-yellow-500 to-yellow-600"
        />
        <StatCard
          title="Cette semaine"
          value={data?.totals?.thisWeek || 0}
          icon={CalendarRange}
          accent="from-blue-500 to-blue-600"
        />
        <StatCard
          title="Ce mois"
          value={data?.totals?.thisMonth || 0}
          icon={Calendar}
          accent="from-emerald-500 to-emerald-600"
        />
        <StatCard
          title="Cette année"
          value={data?.totals?.thisYear || 0}
          icon={BarChart3}
          accent="from-purple-500 to-purple-600"
        />
        <StatCard
          title="Total"
          value={data?.totals?.allTime || 0}
          icon={PieChart}
          accent="from-orange-500 to-orange-600"
        />
      </div>

      <div className="bg-gray-800 border border-gray-700 rounded-xl">
        <div className="flex flex-wrap items-center justify-between border-b border-gray-700 px-4 sm:px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Vue détaillée</h2>
            <p className="text-sm text-gray-400">
              Visualisez la progression des visites selon la période sélectionnée.
            </p>
          </div>
          <div className="flex space-x-2 mt-3 sm:mt-0">
            {viewOptions.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setView(id)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  view === id
                    ? 'bg-yellow-500 text-gray-900'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {currentDataset.length === 0 ? (
            <div className="flex items-center justify-center h-40">
              <p className="text-gray-400">Aucune donnée disponible pour cette période.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {currentDataset.map((item, index) => {
                const visits = Number(item.visits || 0);
                const percent =
                  maxVisits > 0 ? Math.max(6, Math.round((visits / maxVisits) * 100)) : 0;

                return (
                  <motion.div
                    key={`${view}-${index}-${item.key || item.date || item.startDate || index}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.03 }}
                    className="bg-gray-900/50 border border-gray-700 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-300">{formatLabel(item)}</span>
                      <span className="text-lg font-semibold text-white">
                        {visits.toLocaleString('fr-FR')}
                      </span>
                    </div>
                    <div className="h-2 w-full bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, accent }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className="bg-gray-800 border border-gray-700 rounded-xl p-5"
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-400">{title}</p>
        <p className="text-2xl font-bold text-white mt-1">{Number(value || 0).toLocaleString('fr-FR')}</p>
      </div>
      <div
        className={`w-12 h-12 rounded-lg bg-gradient-to-br ${accent} flex items-center justify-center`}
      >
        <Icon className="h-6 w-6 text-white" />
      </div>
    </div>
  </motion.div>
);

export default AdminAnalyticsPage;

